import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Button from "../Generic/Button";
import PrimaryButton from "../Generic/PrimaryButton";
import Loader from "../Generic/Loader";
import EmptyState from "../Generic/EmptyState";
import { errorNotification, successNotification } from "../Generic/Notifications";
import { parseDetailsConfig } from "../Generic/DetailsPageComponents/parseDetailsConfig";
import { AppRoutes, DashboardModules } from "../config/app.config";
import { zIndex } from "../config/zIndex";
import { OfferField, OfferStatus } from "./constants";
import OffersService from "./Services/OffersService";
import { mapOfferResponseToDraft, mapOfferResponseToView } from "./utils/offerApiMapping";
import { getOfferDetailsConfig } from "./config/offerDetailsConfig";
import OfferStatusBadge from "./Components/shared/OfferStatusBadge";
import { useOfferContext } from "./context/OfferContext";
import { canGoBackInSession } from "./utils/offersListFilters";

const OFFERS_ROUTES = AppRoutes[DashboardModules.OFFERS];

const Page = styled.div`
	position: relative;
	min-height: 100%;
	box-sizing: border-box;
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1.5rem;
	gap: 1.5rem;
	overflow-y: auto;
	overflow-x: hidden;
`;

const LoadingOverlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(247, 247, 248, 0.72);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.loadingOverlay};
	pointer-events: all;
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	gap: 1rem;
	flex-wrap: wrap;
`;

const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;
`;

const BackArrow = styled.span.attrs({
	className: "material-symbols-outlined",
})`
	cursor: pointer;
	user-select: none;
	line-height: 1;
`;

const Title = styled.h2`
	font-size: 2rem;
	font-weight: 600;
	color: #36395a;
	margin: 0;
`;

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const OfferDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { setEditingOfferDraft, updateOfferStatusInList } = useOfferContext();
	const [offer, setOffer] = useState(null);
	const [rawOffer, setRawOffer] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [statusUpdating, setStatusUpdating] = useState(false);

	const fetchOffer = useCallback(async () => {

		if (!id) {
			setOffer(null);
			setRawOffer(null);
			setError(true);
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(false);

		const { data, error: fetchError } = await OffersService.getOfferById(id);

		if (fetchError) {
			errorNotification(fetchError);
			setOffer(null);
			setRawOffer(null);
			setError(true);
			setLoading(false);
			return;
		}

		if (!data) {
			setOffer(null);
			setRawOffer(null);
			setError(true);
			setLoading(false);
			return;
		}

		setRawOffer(data);
		setOffer(mapOfferResponseToView(data));
		setLoading(false);
	}, [id]);

	useEffect(() => {
		fetchOffer();
	}, [fetchOffer]);

	const status = offer?.[OfferField.STATUS];
	const publishDisabled =
		statusUpdating || status === OfferStatus.PUBLISHED || status === OfferStatus.EXPIRED;
	const deactivateDisabled =
		statusUpdating || status === OfferStatus.INACTIVE || status === OfferStatus.EXPIRED;

	const applyStatusChange = useCallback(
		async (newStatus, successMessage) => {
			setStatusUpdating(true);
			try {
				const { error: statusError } = await OffersService.changeOfferStatus(
					id,
					Number(newStatus)
				);

				if (statusError) {
					errorNotification(statusError);
					return;
				}

				const statusStr = String(newStatus);
				setOffer((prev) =>
					prev ? { ...prev, [OfferField.STATUS]: statusStr } : prev
				);
				setRawOffer((prev) => (prev ? { ...prev, status: statusStr } : prev));
				updateOfferStatusInList(id, statusStr);
				successNotification(successMessage);
			} finally {
				setStatusUpdating(false);
			}
		},
		[id, updateOfferStatusInList]
	);

	function handlePublish() {
		if (publishDisabled) return;
		applyStatusChange(OfferStatus.PUBLISHED, "Offer published");
	}

	function handleDeactivate() {
		if (deactivateDisabled) return;
		applyStatusChange(OfferStatus.INACTIVE, "Offer deactivated");
	}

	function handleEdit() {
		if (!rawOffer) return;
		setEditingOfferDraft(mapOfferResponseToDraft(rawOffer));
		navigate(OFFERS_ROUTES.EDIT.getPath(offer?.id || id));
	}

	function handleBack() {
		if (canGoBackInSession()) {
			navigate(-1);
			return;
		}
		navigate(OFFERS_ROUTES.LIST.getPath());
	}

	if (!loading && (error || !offer)) {
		return (
			<Page>
				<EmptyState message="Could not fetch offer details" retry={fetchOffer} />
			</Page>
		);
	}

	if (loading && !offer && !error) {
		return (
			<Page>
				<LoadingOverlay>
					<Loader />
				</LoadingOverlay>
			</Page>
		);
	}

	return (
		<Page>
			{(loading || statusUpdating) && (
				<LoadingOverlay>
					<Loader />
				</LoadingOverlay>
			)}
			<Header>
				<TitleRow>
					<BackArrow onClick={handleBack}>
						arrow_back
					</BackArrow>
					<Title>{offer[OfferField.TITLE] || "Offer Details"}</Title>
					<OfferStatusBadge status={offer[OfferField.STATUS]} />
				</TitleRow>
				<HeaderActions>
					<Button disabled={publishDisabled} onClick={handlePublish}>
						Publish
					</Button>
					<Button disabled={deactivateDisabled} onClick={handleDeactivate}>
						Deactivate
					</Button>
					<PrimaryButton onClick={handleEdit}>
						Edit
					</PrimaryButton>
				</HeaderActions>
			</Header>

			{parseDetailsConfig(getOfferDetailsConfig(), offer)}
		</Page>
	);
};

export default OfferDetails;
